import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface AuthSettings {
  registration_enabled: boolean;
  google_oauth_enabled: boolean;
  google_client_id: string | null;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
  }
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authSettings, setAuthSettings] = useState<AuthSettings | null>(null);
  const { login, googleLogin } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch auth settings
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API_URL}/auth/settings`);
        setAuthSettings(response.data);
      } catch (error) {
        console.error('Failed to fetch auth settings');
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    // Initialize Google Sign-In only if enabled
    if (!authSettings?.google_oauth_enabled || !authSettings?.google_client_id) {
      return;
    }

    const initGoogleSignIn = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: authSettings.google_client_id,
          callback: handleGoogleCallback,
        });

        const googleButton = document.getElementById('google-signin-button');
        if (googleButton) {
          window.google.accounts.id.renderButton(googleButton, {
            theme: 'outline',
            size: 'large',
            width: 350,
          });
        }
      }
    };

    // Wait for Google script to load
    const checkGoogle = setInterval(() => {
      if (window.google) {
        clearInterval(checkGoogle);
        initGoogleSignIn();
      }
    }, 100);

    return () => clearInterval(checkGoogle);
  }, [authSettings]);

  const handleGoogleCallback = async (response: any) => {
    try {
      setLoading(true);
      setError('');
      await googleLogin(response.credential);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">Welcome Back</h1>
        <p className="auth-subtitle">Sign in to your NetWorth account</p>

        {authSettings?.google_oauth_enabled && (
          <>
            <div id="google-signin-button" style={{ display: 'flex', justifyContent: 'center' }}></div>
            <div className="auth-divider">or</div>
          </>
        )}

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            color: '#ef4444',
            padding: '0.75rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            style={{ width: '100%' }}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {(authSettings?.registration_enabled !== false) && (
          <p className="auth-footer">
            Don't have an account? <Link to="/register">Sign up</Link>
          </p>
        )}
      </div>
    </div>
  );
};

export default Login;
